import type { WsEvent } from '@chatbot/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';

export type WsStatus = 'connecting' | 'open' | 'closed' | 'degraded';

export interface UseWebSocketProps {
  token: string | null;
  enabled: boolean;
}

export function useWebSocket({ token, enabled }: UseWebSocketProps) {
  const [status, setStatus] = useState<WsStatus>('closed');
  const [lastReconnectAt, setLastReconnectAt] = useState<number | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectDelayRef = useRef<number>(1000);
  const failureCountRef = useRef<number>(0);
  const listenersRef = useRef<Set<(event: WsEvent) => void>>(new Set());

  // Subscription helper
  const subscribe = useCallback((cb: (event: WsEvent) => void) => {
    listenersRef.current.add(cb);
    return () => {
      listenersRef.current.delete(cb);
    };
  }, []);

  const connect = useCallback(() => {
    if (!enabled || !token) {
      setStatus('closed');
      return;
    }

    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    const wsEndpoint = import.meta.env.VITE_WS_ENDPOINT;
    if (!wsEndpoint) {
      console.warn('VITE_WS_ENDPOINT is not set');
      setStatus('degraded');
      return;
    }

    // wss:// endpoint with token Cognito in queryString
    const wsUrl = `${wsEndpoint}?token=${token}`;
    setStatus(failureCountRef.current >= 3 ? 'degraded' : 'connecting');

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        setStatus('open');
        // Record reconnect timestamp if we had previous failures/reconnections
        if (failureCountRef.current > 0) {
          setLastReconnectAt(Date.now());
        }
        failureCountRef.current = 0;
        reconnectDelayRef.current = 1000;
      };

      socket.onmessage = (eventMessage) => {
        try {
          const data: WsEvent = JSON.parse(eventMessage.data);
          for (const listener of listenersRef.current) {
            try {
              listener(data);
            } catch (err) {
              console.error('Error in WS subscriber callback:', err);
            }
          }
        } catch (err) {
          console.warn('ws-client: failed to parse incoming WS message:', err);
        }
      };

      socket.onerror = () => {
        // ws status changes handled by onclose
      };

      socket.onclose = () => {
        socketRef.current = null;
        failureCountRef.current += 1;

        if (failureCountRef.current >= 3) {
          setStatus('degraded');
        } else {
          setStatus('connecting');
        }

        // Exponential backoff reconnect
        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * 2, 30000);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    } catch (err) {
      console.error('ws-client: failed to instantiate WebSocket:', err);
      setStatus('degraded');
    }
  }, [token, enabled]);

  // Handle active socket instantiation on token or enabled change
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setStatus('closed');
      failureCountRef.current = 0;
      reconnectDelayRef.current = 1000;
    };
  }, [connect]);

  return {
    status,
    lastReconnectAt,
    subscribe,
  };
}
